import { isSafeInternalUrl } from '../auth/return-to';
/**
 * The access decision for project-dependent screens, and the return-to plumbing
 * that gets a user back where they were aiming.
 *
 * Joel: "i don't like the idea of being able to click on the various tabs that
 * require project data when there is no selected project." Temperature, Graph,
 * Curing, Subviews, Notes, Health and Info all render a cure. Reached with no
 * project they render an empty shell that looks like the app is broken rather
 * than like the user has not chosen anything yet.
 *
 * PURE AND ROUTER-FREE, deliberately, and modelled on decideAccess() in
 * user-auth.guard: the decision is the part worth testing, and a test that has to
 * stand up a Router and MSAL to ask "may I open this?" is a test nobody writes.
 * The guard performs the navigation; this decides it.
 */

/** Where project selection lives. One constant, so nothing hardcodes the path twice. */
export const SELECTION_ROUTE = '/projects';

/**
 * Return-to is NOT ours and NOT a query parameter.
 *
 * web.ui built `auth/return-to.ts` as the single in-app implementation for this
 * guard to call into (cec5c9a), and their argument is better than the one this
 * module originally shipped: `?returnTo=` leaks into logs, pasted links and
 * referrers, and an attacker-supplied value is an open redirect unless every
 * consumer validates it. sessionStorage keeps it out of the URL entirely, so the
 * validation is the only gate rather than one of several.
 *
 * A second copy that has to agree with theirs is the failure mode this codebase has
 * already paid for twice. Import from '../auth/return-to'.
 */

/**
 * Tabs that cannot render without a project. `info` is included: it is the
 * project's own metadata panel, and with nothing selected it shows a table of
 * blanks.
 */
export const PROJECT_DEPENDENT_TABS: readonly string[] = [
  'temperature',
  'graph',
  'curing',
  'subviews',
  'notes',
  'health',
  'info',
];

export type ProjectAccessDecision =
  /** A project is selected (or the route does not need one) — proceed. */
  | { kind: 'allow' }
  /**
   * State has not settled yet. The guard must WAIT, not redirect.
   *
   * This is the discipline the auth guard was rewritten around, and the reason it
   * is a distinct outcome rather than a falsy "deny": answering "no project" while
   * the restore is still in flight sends the user to the picker a beat before the
   * app works out which project they already had. The auth guard's version of that
   * mistake produced 99 document loads.
   */
  | { kind: 'wait' }
  /**
   * No project, and we know it. Go to selection.
   *
   * `intendedUrl` is handed back for the caller to hand to
   * `rememberIntendedUrl` — this module decides, the caller performs the effects,
   * and the storing/validating of that URL belongs to auth/return-to.
   */
  | { kind: 'redirect'; to: string; intendedUrl: string };

export interface ProjectAccessInput {
  /** Is a project currently selected? */
  projectSelected: boolean;
  /**
   * Has selection state finished resolving — project list loaded, any deep link or
   * persisted selection applied? False means "do not know yet", NOT "no".
   */
  selectionResolved: boolean;
  /** The URL the user was trying to reach, for the return trip. */
  intendedUrl: string;
}

export function decideProjectAccess(input: ProjectAccessInput): ProjectAccessDecision {
  if (input.projectSelected) {
    // Checked BEFORE `selectionResolved`: a user who already has a project open
    // and clicks another tab must never be made to wait on a reload.
    return { kind: 'allow' };
  }

  if (!input.selectionResolved) {
    return { kind: 'wait' };
  }

  return { kind: 'redirect', to: SELECTION_ROUTE, intendedUrl: input.intendedUrl };
}

/**
 * Is a URL worth returning to after selection?
 *
 * Same-origin validation lives in auth/return-to.isSafeInternalUrl; the ONE thing
 * this adds is refusing the selection route itself, which is a loop the moment a
 * project is chosen — and that is a fact about this gate, not about URLs.
 */
export function isWorthReturningTo(url: string | null | undefined): boolean {
  if (!isSafeInternalUrl(url)) return false;
  const path = (url as string).split('?')[0].split('#')[0];
  return path !== SELECTION_ROUTE && path !== `${SELECTION_ROUTE}/`;
}

/**
 * Where to go once a project has been chosen: back where they were aiming, or the
 * default tab when they arrived at selection on purpose.
 */
export function destinationAfterSelection(
  returnTo: string | null | undefined,
  defaultRoute = '/temperature'
): string {
  return isWorthReturningTo(returnTo) ? (returnTo as string) : defaultRoute;
}

/**
 * Is this tab openable right now? Drives the DISABLED state in the nav.
 *
 * Disabled, never hidden. A tab that vanishes teaches the user the feature does
 * not exist; a tab that is visibly greyed with a reason teaches them what to do
 * next. That is Joel's established pattern from the desktop, and it is also the
 * difference between "this app is missing things" and "I have not chosen a
 * project yet".
 */
export function isTabEnabled(tab: string, projectSelected: boolean): boolean {
  return projectSelected || !PROJECT_DEPENDENT_TABS.includes(tab);
}

/** The tooltip a disabled tab carries. Says what to do, not what went wrong. */
export const NO_PROJECT_TAB_REASON = 'Select a project first';
